/**
 * Build jQuery UI datepicker options from field.date_picker_settings (block checkout).
 */

const DATEPICKER_FORMAT_PRESETS = ['yy-mm-dd', 'mm/dd/yy', 'd MM, y', 'd M, y', 'DD, d MM, yy'];

function getJQueryDatepicker() {
    if (typeof window === 'undefined' || !window.jQuery || typeof window.jQuery.fn.datepicker !== 'function') {
        return null;
    }
    return window.jQuery;
}

export function isValidDatepickerFormat(format) {
    const value = String(format || '').trim();
    if (!value) {
        return false;
    }
    if (DATEPICKER_FORMAT_PRESETS.indexOf(value) !== -1) {
        return true;
    }
    return /[dDmMyY@!]/.test(value);
}

export function isValidDatepickerOffset(offset) {
    const value = String(offset || '').trim();
    if (!value) {
        return true;
    }
    return /^(?:[+\-]?\d+\s*[dmwy]?)(?:\s+[+\-]?\d+\s*[dmwy]?)*$/i.test(value);
}

export function normalizeDatepickerYearRange(range) {
    const value = String(range || '').trim();
    if (!value || value.indexOf(':') === -1) {
        return value;
    }

    const parts = value.split(':');
    if (parts.length !== 2) {
        return value;
    }

    const normalizePart = (part) => {
        const trimmed = String(part || '').trim();
        if (!trimmed) {
            return trimmed;
        }
        if (/^c[+\-]/i.test(trimmed)) {
            return trimmed;
        }
        if (/^[+\-]/.test(trimmed)) {
            return trimmed;
        }
        if (/^\d{4}$/.test(trimmed)) {
            return trimmed;
        }
        if (/^\d{1,3}$/.test(trimmed)) {
            return `+${trimmed}`;
        }
        return trimmed;
    };

    return `${normalizePart(parts[0])}:${normalizePart(parts[1])}`;
}

export function isValidDatepickerYearRange(range) {
    const value = normalizeDatepickerYearRange(range);
    if (!value) {
        return true;
    }
    return /^([+\-]\d+|c[+\-]\d+|\d{4}):([+\-]\d+|c[+\-]\d+|\d{4})$/i.test(value);
}

export function expandDatepickerYearRange(range, currentYear, selectedYear) {
    const normalized = normalizeDatepickerYearRange(range);
    if (!normalized || normalized.indexOf(':') === -1) {
        return null;
    }

    const parts = normalized.split(':');
    if (parts.length !== 2) {
        return null;
    }

    const now = new Date();
    const thisYear = currentYear || now.getFullYear();
    const drawYear = selectedYear || thisYear;

    const resolve = (value) => {
        const trimmed = String(value || '').trim();
        if (/^c([+\-].*)$/i.test(trimmed)) {
            return drawYear + parseInt(trimmed.slice(1), 10);
        }
        if (/^[+\-]/.test(trimmed)) {
            return thisYear + parseInt(trimmed, 10);
        }
        const year = parseInt(trimmed, 10);
        return year || thisYear;
    };

    const minYear = resolve(parts[0]);
    const maxYear = Math.max(minYear, resolve(parts[1]));

    return { minYear, maxYear };
}

export function isFixedDatepickerYearRange(range) {
    const normalized = normalizeDatepickerYearRange(range);
    return /^\d{4}:\d{4}$/.test(normalized);
}

function buildYearRangeBeforeShow(options) {
    const minYear = options.yearRangeMinYear;
    const maxYear = options.yearRangeMaxYear;
    if (!minYear || !maxYear) {
        return null;
    }

    return function beforeShow(input, inst) {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        let targetYear = currentYear;
        let targetMonth = currentMonth;

        if (currentYear < minYear) {
            targetYear = minYear;
            targetMonth = 0;
        } else if (currentYear > maxYear) {
            targetYear = maxYear;
            targetMonth = 11;
        }

        inst.drawYear = targetYear;
        inst.selectedYear = targetYear;
        inst.drawMonth = targetMonth;
        inst.selectedMonth = targetMonth;
    };
}

function resolveEffectiveDatepickerBoundYear(value, dateFormat) {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    if (value === 0 || value === '0') {
        return new Date().getFullYear();
    }
    const resolved = tryResolveDatepickerValue(value, dateFormat);
    if (resolved === 0) {
        return new Date().getFullYear();
    }
    if (resolved instanceof Date && !Number.isNaN(resolved.getTime())) {
        return resolved.getFullYear();
    }
    return null;
}

function isDateBeforeEffectiveBound(date, bound, dateFormat) {
    if (bound === undefined || bound === null || bound === '') {
        return false;
    }
    if (bound === 0 || bound === '0') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date.getTime() < today.getTime();
    }
    const resolved = tryResolveDatepickerValue(bound, dateFormat);
    if (resolved === 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date.getTime() < today.getTime();
    }
    if (resolved instanceof Date && !Number.isNaN(resolved.getTime())) {
        const boundDate = new Date(resolved.getTime());
        boundDate.setHours(0, 0, 0, 0);
        return date.getTime() < boundDate.getTime();
    }
    return false;
}

function isDateAfterEffectiveBound(date, bound, dateFormat, minDate) {
    if (bound === undefined || bound === null || bound === '') {
        return false;
    }
    const resolved = tryResolveDatepickerValue(bound, dateFormat, minDate);
    if (resolved instanceof Date && !Number.isNaN(resolved.getTime())) {
        const boundDate = new Date(resolved.getTime());
        boundDate.setHours(23, 59, 59, 999);
        return date.getTime() > boundDate.getTime();
    }
    return false;
}

function wrapBeforeShowDayWithEffectiveBounds(beforeShowDay, options) {
    const effectiveMinDate = options.effectiveMinDate;
    const effectiveMaxDate = options.effectiveMaxDate;
    const dateFormat = options.dateFormat || 'yy-mm-dd';
    const minDate = options.minDate;

    if (
        (effectiveMinDate === undefined || effectiveMinDate === null || effectiveMinDate === '')
        && (effectiveMaxDate === undefined || effectiveMaxDate === null || effectiveMaxDate === '')
    ) {
        return beforeShowDay || null;
    }

    return function beforeShowDayWithBounds(date) {
        if (typeof beforeShowDay === 'function') {
            const result = beforeShowDay(date);
            if (!result[0]) {
                return result;
            }
        }

        if (isDateBeforeEffectiveBound(date, effectiveMinDate, dateFormat)) {
            return [false, ''];
        }

        if (isDateAfterEffectiveBound(date, effectiveMaxDate, dateFormat, minDate)) {
            return [false, ''];
        }

        return [true, ''];
    };
}

function applyExpandedYearRangeOptions(options, settings, field, expanded) {
    if (!expanded) {
        return;
    }

    options.changeYear = true;
    options.yearRange = `${expanded.minYear}:${expanded.maxYear}`;
    options.yearRangeMinYear = expanded.minYear;
    options.yearRangeMaxYear = expanded.maxYear;

    const dateFormat = options.dateFormat || 'yy-mm-dd';
    const hasExplicitMin = !!settings.min_date;
    const hasExplicitMax = !!settings.max_date;
    const isFixed = isFixedDatepickerYearRange(settings.year_range);

    if (isFixed) {
        if (!hasExplicitMax) {
            const maxDate = formatDatepickerDate(expanded.maxYear, 12, 31, dateFormat);
            if (maxDate) {
                options.maxDate = maxDate;
            }
        }
        if (!hasExplicitMin) {
            const minDate = formatDatepickerDate(expanded.minYear, 1, 1, dateFormat);
            if (minDate) {
                options.minDate = minDate;
            }
        }
        return;
    }

    let effectiveMinDate;
    if (hasExplicitMin) {
        effectiveMinDate = settings.min_date;
    } else if (field.is_min_date == 0 || field.is_min_date === false) {
        effectiveMinDate = 0;
    }

    let effectiveMaxDate;
    if (hasExplicitMax) {
        effectiveMaxDate = settings.max_date;
    }

    const effectiveMinYear = resolveEffectiveDatepickerBoundYear(effectiveMinDate, dateFormat);
    if (effectiveMinYear !== null && expanded.minYear < effectiveMinYear) {
        const dropdownMinDate = formatDatepickerDate(expanded.minYear, 1, 1, dateFormat);
        if (dropdownMinDate) {
            options.minDate = dropdownMinDate;
            options.effectiveMinDate = effectiveMinDate;
        }
    }

    const effectiveMaxYear = resolveEffectiveDatepickerBoundYear(effectiveMaxDate, dateFormat);
    if (effectiveMaxYear !== null && expanded.maxYear > effectiveMaxYear) {
        const dropdownMaxDate = formatDatepickerDate(expanded.maxYear, 12, 31, dateFormat);
        if (dropdownMaxDate) {
            options.maxDate = dropdownMaxDate;
            options.effectiveMaxDate = effectiveMaxDate;
        }
    }
}

export function isValidDatepickerDisabledDates(dates) {
    const value = String(dates || '').trim();
    if (!value) {
        return true;
    }
    return value.split(',').every((part) => {
        const date = part.trim();
        if (!date) {
            return true;
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return false;
        }
        const chunks = date.split('-');
        const year = parseInt(chunks[0], 10);
        const month = parseInt(chunks[1], 10);
        const day = parseInt(chunks[2], 10);
        const testDate = new Date(year, month - 1, day);
        return testDate.getFullYear() === year && testDate.getMonth() === (month - 1) && testDate.getDate() === day;
    });
}

export function filterValidDatePickerSettings(settings = {}) {
    const dateFormat = String(settings.date_format || 'yy-mm-dd').trim();
    const defaultDate = String(settings.default_date || '').trim();
    const minDate = String(settings.min_date || '').trim();
    const maxDate = String(settings.max_date || '').trim();
    const yearRange = normalizeDatepickerYearRange(settings.year_range || '');
    const disabledDates = String(settings.disabled_dates || '').trim();
    const months = parseInt(settings.number_of_months, 10) || 1;
    const disabledDays = Array.isArray(settings.disabled_days)
        ? settings.disabled_days.map(Number).filter((day) => day >= 0 && day <= 6)
        : [];

    return {
        date_format: isValidDatepickerFormat(dateFormat) ? dateFormat : 'yy-mm-dd',
        default_date: isValidDatepickerOffset(defaultDate) ? defaultDate : '',
        min_date: isValidDatepickerOffset(minDate) ? minDate : '',
        max_date: isValidDatepickerOffset(maxDate) ? maxDate : '',
        year_range: isValidDatepickerYearRange(yearRange) ? yearRange : '',
        number_of_months: String(Math.max(1, Math.min(3, months))),
        disabled_days: disabledDays,
        disabled_dates: isValidDatepickerDisabledDates(disabledDates) ? disabledDates : '',
    };
}

/**
 * Resolve a relative default-date offset (e.g. +7, +1m +7d) to a formatted date string.
 *
 * @param {string} offset
 * @param {string|number} minDate
 * @param {string} dateFormat
 * @returns {string}
 */
export function resolveDefaultDateOffset(offset, minDate, dateFormat) {
    if (!offset || typeof offset !== 'string' || !offset.trim()) {
        return '';
    }

    const $ = getJQueryDatepicker();
    if (!$) {
        return '';
    }

    const format = dateFormat || 'yy-mm-dd';
    const $tmp = $('<input type="text" style="position:absolute;left:-9999px;top:-9999px;" aria-hidden="true">').appendTo('body');

    try {
        const opts = { dateFormat: format };
        if (minDate !== undefined && minDate !== null && minDate !== '') {
            opts.minDate = minDate;
        }
        $tmp.datepicker(opts);

        const inst = $.datepicker._getInst($tmp[0]);
        const date = $.datepicker._determineDate(inst, offset.trim(), new Date());
        if (!date) {
            return '';
        }
        return $.datepicker.formatDate(format, date);
    } catch (e) {
        return '';
    } finally {
        try {
            $tmp.datepicker('destroy');
        } catch (destroyErr) {
            // ignore
        }
        $tmp.remove();
    }
}

/**
 * Resolve default date string from a checkout field definition.
 *
 * @param {object} field
 * @returns {string}
 */
/**
 * Normalize a stored user-meta / saved date for jQuery UI datepicker display.
 * Parses common storage formats and outputs the field's configured dateFormat.
 *
 * @param {object} field
 * @param {string|number} rawValue
 * @returns {string}
 */
export function formatStoredDateForDatepicker(field, rawValue) {
    if (rawValue === undefined || rawValue === null || rawValue === '') {
        return '';
    }

    const value = String(rawValue).trim();
    if (!value) {
        return '';
    }

    const settings = filterValidDatePickerSettings(field?.date_picker_settings || {});
    const dateFormat = settings.date_format || 'yy-mm-dd';

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const year = parseInt(isoMatch[1], 10);
        const month = parseInt(isoMatch[2], 10);
        const day = parseInt(isoMatch[3], 10);
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() === year && date.getMonth() === (month - 1) && date.getDate() === day) {
            return formatDatepickerDate(year, month, day, dateFormat);
        }
    }

    const resolved = tryResolveDatepickerValue(value, dateFormat);
    if (resolved instanceof Date && !Number.isNaN(resolved.getTime())) {
        return formatDatepickerDate(
            resolved.getFullYear(),
            resolved.getMonth() + 1,
            resolved.getDate(),
            dateFormat
        );
    }

    const loose = new Date(value);
    if (!Number.isNaN(loose.getTime())) {
        return formatDatepickerDate(
            loose.getFullYear(),
            loose.getMonth() + 1,
            loose.getDate(),
            dateFormat
        );
    }

    return value;
}

export function resolveDefaultDateFromField(field) {
    const settings = filterValidDatePickerSettings(field?.date_picker_settings || {});
    if (!settings.default_date) {
        return '';
    }

    const minDate = settings.min_date
        || ((field.is_min_date == 0 || field.is_min_date === false) ? 0 : '');

    return resolveDefaultDateOffset(
        settings.default_date,
        minDate,
        settings.date_format || 'yy-mm-dd'
    );
}

/**
 * Format a calendar date using the active jQuery UI dateFormat string.
 *
 * @param {number} year
 * @param {number} month 1-12
 * @param {number} day
 * @param {string} dateFormat
 * @returns {string}
 */
export function formatDatepickerDate(year, month, day, dateFormat) {
    const $ = getJQueryDatepicker();
    if (!$ || typeof $.datepicker?.formatDate !== 'function') {
        return '';
    }

    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    try {
        return $.datepicker.formatDate(dateFormat || 'yy-mm-dd', date);
    } catch (e) {
        return '';
    }
}

/**
 * Resolve a jQuery UI minDate/maxDate/defaultDate value to a Date (or 0 for today).
 *
 * @param {string|number} value
 * @param {string} dateFormat
 * @param {string|number|null|undefined} minDate
 * @returns {Date|number|null}
 */
export function tryResolveDatepickerValue(value, dateFormat, minDate) {
    if (value === 0 || value === '0') {
        return 0;
    }

    if (value === undefined || value === null || value === '') {
        return null;
    }

    const $ = getJQueryDatepicker();
    if (!$ || typeof $.fn.datepicker !== 'function') {
        return null;
    }

    const format = dateFormat || 'yy-mm-dd';
    const $tmp = $('<input type="text" style="position:absolute;left:-9999px;top:-9999px;" aria-hidden="true">').appendTo('body');

    try {
        const opts = { dateFormat: format };
        if (minDate !== undefined && minDate !== null && minDate !== '') {
            opts.minDate = minDate;
        }
        $tmp.datepicker(opts);

        const inst = $.datepicker._getInst($tmp[0]);
        const date = $.datepicker._determineDate(inst, String(value).trim(), new Date());
        if (!date) {
            return null;
        }
        return date;
    } catch (e) {
        return null;
    } finally {
        try {
            $tmp.datepicker('destroy');
        } catch (destroyErr) {
            // ignore
        }
        $tmp.remove();
    }
}

/**
 * Whether a jQuery UI date bound can be parsed in the current dateFormat.
 *
 * @param {string|number} value
 * @param {string} dateFormat
 * @param {string|number|null|undefined} minDate
 * @returns {boolean}
 */
export function isApplicableDatepickerBound(value, dateFormat, minDate) {
    if (value === 0 || value === '0') {
        return true;
    }
    if (value === undefined || value === null || String(value).trim() === '') {
        return false;
    }
    return tryResolveDatepickerValue(value, dateFormat, minDate) !== null;
}

/**
 * Compare resolved jQuery UI date bounds (0 = today).
 *
 * @param {Date|number} left
 * @param {Date|number} right
 * @returns {number}
 */
function compareDatepickerBounds(left, right) {
    const leftTime = left === 0 ? new Date().setHours(0, 0, 0, 0) : left.getTime();
    const rightTime = right === 0 ? new Date().setHours(0, 0, 0, 0) : right.getTime();
    return leftTime - rightTime;
}

/**
 * Drop invalid saved options so a bad min/max/year range cannot disable every date.
 *
 * @param {object} options
 * @returns {object}
 */
export function sanitizeDatepickerOptions(options = {}) {
    const dateFormat = options.dateFormat || 'yy-mm-dd';
    const sanitized = {
        dateFormat,
        numberOfMonths: Math.max(1, Math.min(3, parseInt(options.numberOfMonths, 10) || 1)),
    };

    const $ = getJQueryDatepicker();
    const canTest = $ && typeof $.fn.datepicker === 'function';

    if (options.yearRange && isValidDatepickerYearRange(options.yearRange)) {
        const normalizedYearRange = normalizeDatepickerYearRange(options.yearRange);
        let yearRangeOk = true;

        if (canTest) {
            const $tmp = $('<input type="text" style="position:absolute;left:-9999px;top:-9999px;" aria-hidden="true">').appendTo('body');
            try {
                $tmp.datepicker({ changeYear: true, yearRange: normalizedYearRange });
                $tmp.datepicker('destroy');
            } catch (e) {
                yearRangeOk = false;
            }
            $tmp.remove();
        }

        if (yearRangeOk) {
            sanitized.yearRange = normalizedYearRange;
        }
    }

    let minDate = options.minDate;
    if (minDate !== undefined && minDate !== null && minDate !== '') {
        if (canTest && isApplicableDatepickerBound(minDate, dateFormat)) {
            sanitized.minDate = minDate;
        } else if (!canTest && (minDate === 0 || minDate === '0' || isValidDatepickerOffset(String(minDate)))) {
            sanitized.minDate = minDate;
        }
    }

    let maxDate = options.maxDate;
    if (maxDate !== undefined && maxDate !== null && maxDate !== '') {
        if (canTest && isApplicableDatepickerBound(maxDate, dateFormat, sanitized.minDate)) {
            sanitized.maxDate = maxDate;
        } else if (!canTest && isValidDatepickerOffset(String(maxDate))) {
            sanitized.maxDate = maxDate;
        }
    }

    if (canTest && sanitized.minDate !== undefined && sanitized.maxDate !== undefined) {
        const resolvedMin = tryResolveDatepickerValue(sanitized.minDate, dateFormat);
        const resolvedMax = tryResolveDatepickerValue(sanitized.maxDate, dateFormat, sanitized.minDate);
        if (resolvedMin !== null && resolvedMax !== null && compareDatepickerBounds(resolvedMin, resolvedMax) > 0) {
            delete sanitized.maxDate;
        }
    }

    if (options.defaultDate !== undefined && options.defaultDate !== null && String(options.defaultDate).trim() !== '') {
        const defaultDate = String(options.defaultDate).trim();
        if (canTest && isApplicableDatepickerBound(defaultDate, dateFormat, sanitized.minDate)) {
            const resolvedDefault = tryResolveDatepickerValue(defaultDate, dateFormat, sanitized.minDate);
            let includeDefault = true;

            if (resolvedDefault !== null) {
                if (sanitized.minDate !== undefined) {
                    const resolvedMin = tryResolveDatepickerValue(sanitized.minDate, dateFormat);
                    if (resolvedMin !== null && compareDatepickerBounds(resolvedDefault, resolvedMin) < 0) {
                        includeDefault = false;
                    }
                }
                if (includeDefault && sanitized.maxDate !== undefined) {
                    const resolvedMax = tryResolveDatepickerValue(sanitized.maxDate, dateFormat, sanitized.minDate);
                    if (resolvedMax !== null && compareDatepickerBounds(resolvedDefault, resolvedMax) > 0) {
                        includeDefault = false;
                    }
                }
            }

            if (includeDefault) {
                sanitized.defaultDate = defaultDate;
            }
        } else if (!canTest && isValidDatepickerOffset(defaultDate)) {
            sanitized.defaultDate = defaultDate;
        }
    }

    if (options.yearRangeMinYear && options.yearRangeMaxYear) {
        const minYear = parseInt(options.yearRangeMinYear, 10);
        const maxYear = parseInt(options.yearRangeMaxYear, 10);
        if (minYear && maxYear && minYear <= maxYear && sanitized.yearRange) {
            sanitized.yearRangeMinYear = minYear;
            sanitized.yearRangeMaxYear = maxYear;
        }
    }

    if (options.changeYear) {
        sanitized.changeYear = true;
    }

    if (options.effectiveMinDate !== undefined && options.effectiveMinDate !== null && options.effectiveMinDate !== '') {
        sanitized.effectiveMinDate = options.effectiveMinDate;
    }

    if (options.effectiveMaxDate !== undefined && options.effectiveMaxDate !== null && options.effectiveMaxDate !== '') {
        sanitized.effectiveMaxDate = options.effectiveMaxDate;
    }

    if (typeof options.beforeShowDay === 'function') {
        sanitized.beforeShowDay = options.beforeShowDay;
    }

    if (typeof options.beforeShow === 'function' && sanitized.yearRangeMinYear && sanitized.yearRangeMaxYear) {
        sanitized.beforeShow = options.beforeShow;
    }

    if (typeof options.onSelect === 'function') {
        sanitized.onSelect = options.onSelect;
    }

    return sanitized;
}

/**
 * Apply resolved default date to a jQuery datepicker input when empty.
 *
 * @param {object} $input jQuery input element
 * @param {object} config datepicker config (defaultDate, minDate, dateFormat)
 * @returns {boolean}
 */
export function applyDefaultDateToInput($input, config) {
    if (!$input || !$input.length) {
        return false;
    }

    const currentVal = ($input.val() || '').trim();
    if (currentVal) {
        return false;
    }

    const defaultDate = config?.defaultDate;
    if (!defaultDate) {
        return false;
    }

    const resolved = resolveDefaultDateOffset(
        defaultDate,
        config.minDate,
        config.dateFormat || 'yy-mm-dd'
    );
    if (!resolved) {
        return false;
    }

    $input.val(resolved);
    if ($input.hasClass('hasDatepicker')) {
        try {
            $input.datepicker('setDate', resolved);
        } catch (e) {
            // Keep typed value when datepicker cannot parse the stored format.
        }
    }

    $input.trigger('change').trigger('input');

    if (typeof window.fmeCcfwRefreshTimepickersForDate === 'function') {
        const fieldName = $input.attr('name') || $input.attr('id') || '';
        window.fmeCcfwRefreshTimepickersForDate(fieldName);
    }

    return true;
}

export function getDatepickerOptions(field) {
    const settings = filterValidDatePickerSettings(field.date_picker_settings || {});
    const options = {
        dateFormat: settings.date_format || 'yy-mm-dd',
        numberOfMonths: Math.max(1, Math.min(3, parseInt(settings.number_of_months, 10) || 1)),
    };

    const hasExplicitMin = !!settings.min_date;
    const hasExplicitMax = !!settings.max_date;

    if (settings.default_date) {
        options.defaultDate = settings.default_date;
    }
    if (hasExplicitMin) {
        options.minDate = settings.min_date;
    } else if (field.is_min_date == 0 || field.is_min_date === false) {
        options.minDate = 0;
    }
    if (hasExplicitMax) {
        options.maxDate = settings.max_date;
    }

    if (settings.year_range) {
        applyExpandedYearRangeOptions(
            options,
            settings,
            field,
            expandDatepickerYearRange(settings.year_range)
        );
    }

    const disabledDays = Array.isArray(settings.disabled_days) ? settings.disabled_days.map(Number) : [];
    const disabledDates = settings.disabled_dates
        ? String(settings.disabled_dates).split(',').map((d) => d.trim()).filter(Boolean)
        : [];

    let disabledBeforeShowDay = null;
    if (disabledDays.length || disabledDates.length) {
        disabledBeforeShowDay = (date) => {
            const day = date.getDay();
            if (disabledDays.indexOf(day) !== -1) {
                return [false, ''];
            }
            if (disabledDates.length) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                const iso = `${y}-${m}-${d}`;
                if (disabledDates.indexOf(iso) !== -1) {
                    return [false, ''];
                }
            }
            return [true, ''];
        };
    }

    options.beforeShowDay = wrapBeforeShowDayWithEffectiveBounds(disabledBeforeShowDay, options);

    const yearRangeBeforeShow = buildYearRangeBeforeShow(options);
    if (yearRangeBeforeShow) {
        options.beforeShow = yearRangeBeforeShow;
    }

    return sanitizeDatepickerOptions(options);
}
